import CountUp from "react-countup";
import { motion } from "framer-motion";
import { Users, Download, Sparkles, Star, Activity } from "lucide-react";
import { useStats } from "../hooks/useStats";
import { APPS } from "../data/apps";
import { Reveal } from "./Shared";
import { AppIcon } from "./AppIcon";

const GROUP_CARDS = [
  { key: "total_users", label: "Utilisateurs", icon: Users, prefix: "Plus de", step: 10 },
  { key: "total_downloads", label: "Téléchargements", icon: Download, prefix: "Plus de", step: 10 },
  { key: "applications", label: "Applications", icon: Sparkles, prefix: "", step: 0 },
  { key: "satisfaction", label: "Satisfaction", icon: Star, prefix: "", step: 0, suffix: "%" },
];

const roundStat = (value, prefix, step) => {
  if (!step) return value;
  return prefix === "Près de" ? Math.round(value / step) * step : Math.floor(value / step) * step;
};

export const StatsDashboard = () => {
  const { stats, error } = useStats(5000);

  return (
    <section data-testid="dashboard-section" id="dashboard" className="relative bg-navy py-20 lg:py-28 text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50">
            <span className="flex h-2 w-2 items-center justify-center">
              <span className="absolute h-2 w-2 rounded-full bg-africain animate-ping" />
              <span className="h-2 w-2 rounded-full bg-africain" />
            </span>
            Tableau de bord — Temps réel
          </div>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
            L'écosystème TIRERA GROUP en chiffres
          </h2>
          <p className="mt-4 max-w-xl text-white/60">
            Toutes nos applications transmettent leurs données à un backend commun. Le site affiche
            automatiquement les statistiques agrégées, mises à jour en continu.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {GROUP_CARDS.map((c, i) => {
            const Icon = c.icon;
            const raw = stats?.group?.[c.key] ?? 0;
            const value = roundStat(raw, c.prefix, c.step);
            return (
              <Reveal key={c.key} i={i}>
                <div
                  data-testid={`group-stat-${c.key}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm h-full"
                >
                  <Icon size={20} className="text-white/40" />
                  {c.prefix && <div className="mt-6 text-xs font-medium text-white/50">{c.prefix}</div>}
                  <div className={`${c.prefix ? "mt-1" : "mt-6"} font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight tabular-nums`}>
                    <CountUp end={value} duration={2} separator=" " preserveValue />{c.suffix || ""}
                  </div>
                  <div className="mt-2 text-sm text-white/60">{c.label}</div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-6 grid gap-4 lg:gap-6 md:grid-cols-2 lg:grid-cols-4">
          {APPS.filter((a) => a.stats).map((a, i) => {
            const primary = a.stats[0];
            const raw = stats?.apps?.[a.slug]?.[primary.key] ?? 0;
            const value = roundStat(raw, a.highlight.qualifier, a.highlight.step);
            return (
              <Reveal key={a.slug} i={i}>
                <div
                  data-testid={`app-stat-${a.slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 h-full"
                >
                  <div className="flex items-center gap-3">
                    <AppIcon app={a} variant="soft" iconSize={17} containerClass="h-9 w-9 rounded-lg" />
                    <span className="font-display font-semibold text-sm">{a.name}</span>
                  </div>
                  <div className="mt-5 text-xs font-medium text-white/50">{a.highlight.qualifier}</div>
                  <div className="mt-1 font-display text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums" style={{ color: a.accent }}>
                    <CountUp end={value} duration={2} separator=" " preserveValue />
                  </div>
                  <div className="mt-1 text-xs text-white/50">{primary.label}</div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-8 flex items-center gap-2 text-xs text-white/40">
          <Activity size={14} />
          {error ? "Reconnexion au backend…" : "Données synchronisées avec le backend commun • actualisation toutes les 5 s"}
        </div>
      </div>
    </section>
  );
};
